<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">Projects</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Create and manage your projects and members.</p>
      </div>
      <AddProject @created="onProjectCreated" @toggle-message="notify" />
    </div>

    <!-- Project list -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <EmptyState
        v-if="projects.length === 0"
        icon="mdi-folder-open-outline"
        title="No projects yet"
        subtitle="Create one to get started."
      />
      <div v-else class="space-y-2">
        <div v-for="project in projects" :key="project.id"
          class="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="truncate text-sm font-bold text-gray-900 dark:text-gray-50">{{ project.title }}</h3>
                <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">{{ project.key }}</span>
              </div>
              <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{{ project.description || 'No description' }}</p>
              <div class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <span class="inline-flex items-center gap-1"><v-icon size="14">mdi-account-multiple</v-icon>{{ (project.member || []).length }} member(s)</span>
                <span class="inline-flex items-center gap-1">
                  <v-icon size="14">mdi-calendar-range</v-icon>
                  <template v-if="project.start_date || project.end_date">{{ project.start_date || '—' }} → {{ project.end_date || '—' }}</template>
                  <span v-else>No timeline set</span>
                </span>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-0.5">
              <EditProjectTimeline :project="project" @updated="onTimelineUpdated" @toggle-message="notify" />
              <EditProjectMembers :project="project" @updated="onMembersUpdated" @toggle-message="notify" />
              <button type="button" aria-label="Delete project" @click="confirmDelete(project)"
                class="rounded-lg p-1.5 text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/10"><v-icon size="18">mdi-delete-outline</v-icon></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card title="Delete project" prepend-icon="mdi-alert">
        <v-card-text>
          Delete <strong>{{ toDelete && toDelete.title }}</strong> and its members? This cannot be undone.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Cancel" variant="plain" @click="deleteDialog = false"></v-btn>
          <v-btn color="red" text="Delete" variant="tonal" @click="deleteProject"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import EmptyState from '@/components/ui/EmptyState.vue';
import Snackbar from './Snackbar.vue';
import AddProject from './AddProject.vue';
import EditProjectMembers from './EditProjectMembers.vue';
import EditProjectTimeline from './EditProjectTimeline.vue';

export default {
  name: 'ProjectPage',
  components: { Snackbar, AddProject, EditProjectMembers, EditProjectTimeline, EmptyState },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant: constant,
      projects: [],
      deleteDialog: false,
      toDelete: null,
      isshow: false,
      message: '',
    };
  },
  methods: {
    notify(msg) {
      this.isshow = true;
      this.message = msg;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    getProjects() {
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => { this.projects = response.data.data || []; })
        .catch(error => console.error('Error fetching projects:', error));
    },
    onProjectCreated() {
      this.getProjects();
    },
    onMembersUpdated() {
      this.getProjects();
    },
    onTimelineUpdated() {
      this.getProjects();
    },
    confirmDelete(project) {
      this.toDelete = project;
      this.deleteDialog = true;
    },
    deleteProject() {
      if (!this.toDelete) return;
      axios.post(utils._api(this.constant.delete_project), { id: this.toDelete.id }, this.authHeaders())
        .then((response) => {
          this.deleteDialog = false;
          this.toDelete = null;
          this.notify(response.data.message);
          this.getProjects();
        })
        .catch(error => {
          console.error('Error deleting project:', error);
          this.notify('Failed to delete project');
        });
    },
  },
  mounted() {
    this.getProjects();
  }
};
</script>
